import { Alert } from '@/components/Alert'
import { Space } from '@/components/Space'
import { Typography } from '@/components/Typography'
import { PATHS } from '@/routes/PATHS'
import { AppError } from '@/utils/classes/AppError'
import { convertAxiosErrorToAppError } from '@/utils/functions/errors/convertAxiosErrorToAppError'
import { isForbiddenError } from '@/utils/functions/errors/isForbiddenError'
import { isNotFoundError } from '@/utils/functions/errors/isNotFoundError'
import { Button } from 'antd'
import { isAxiosError } from 'axios'
import { useMemo } from 'react'
import { type FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useLocation } from 'react-router'

interface GoToPathProps {
  buttonText: string
  pathUrl: string
  fallbackUrl?: never
  showLink?: true
}
interface GoBackProps {
  buttonText?: never
  pathUrl?: never
  fallbackUrl: string
  showLink?: true
}

interface NoLinkProps {
  buttonText?: never
  pathUrl?: never
  fallbackUrl?: never
  showLink: false
}

export type DefaultErrorFallbackNavigationProps = GoToPathProps | GoBackProps | NoLinkProps
export type DefaultErrorFallbackProps = FallbackProps &
  DefaultErrorFallbackNavigationProps & { title?: string }

export const DEFAULT_ERROR_STATE_TEST_ID = 'default-error-state-test-id'

export function DefaultErrorFallback({
  error,
  resetErrorBoundary,
  fallbackUrl,
  buttonText,
  pathUrl,
  showLink = true,
  title,
}: DefaultErrorFallbackProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'components.errorFallback' })
  const location = useLocation()

  const parsedError = useMemo(() => {
    const parsedError = isAxiosError(error) ? convertAxiosErrorToAppError(error) : error

    if (parsedError instanceof AppError) {
      return parsedError
    }

    return new AppError({
      status: 500,
      message: error.message || 'Unknown error',
    })
  }, [error])

  const linkUrl = useMemo(() => {
    if (pathUrl) return pathUrl

    const hasPreviousPage = location.key !== 'default'
    if (hasPreviousPage) return '..'

    return fallbackUrl
  }, [pathUrl, location.key, fallbackUrl])

  const pathToRedirect = useMemo(() => {
    if (isNotFoundError(parsedError)) {
      return PATHS.notFoundPage
    }

    if (isForbiddenError(parsedError)) {
      return PATHS.forbiddenPage
    }
  }, [parsedError])

  if (pathToRedirect) {
    return <Navigate to={pathToRedirect} />
  }

  return (
    <Alert
      data-testid={DEFAULT_ERROR_STATE_TEST_ID}
      showIcon
      type="error"
      message={title || t('title')}
      description={
        <Space direction="vertical" size="small">
          {parsedError?.message && <Typography.Text>{parsedError.message}</Typography.Text>}

          <Space size="small">
            {resetErrorBoundary && (
              <Button size="small" onClick={resetErrorBoundary} danger>
                {t('tryAgain')}
              </Button>
            )}

            {showLink && linkUrl && (
              <Link to={linkUrl} relative="path">
                <Button type="link">{buttonText || t('goBack')}</Button>
              </Link>
            )}
          </Space>
        </Space>
      }
    />
  )
}
